// ─── Chin Global Workspace ────────────────────────────────
// Module-level singleton store that manages order execution
// loops and off (limit) engines. Unlike React component state,
// this store survives tab switches, page navigation, and
// component mount/unmount cycles.
//
// Used by:
//   - useChinGlobalRestore: auto-starts engines on login
//   - ChinMonitor: displays status and handles user controls
//
// Architecture:
//   Module-level Map → survives everything except browser close.
//   All start/abort functions are stored here, not in React refs.
//
// PER-ACCOUNT ISOLATION:
//   A strategy can run on MULTIPLE accounts simultaneously. Order
//   loops and off engines are therefore scoped per
//   (strategyId, accountId, symbolId) so account A's loops never
//   collide with account B's loops on the same strategy + symbol.

import { supabase } from '@/lib/supabase';
import { getLatestPositionsSnapshot } from '@/lib/shared-ctrader-stream';
import { startOrderLoop } from '@/lib/chin-order-executor';
import { startOffEngine } from '@/lib/chin-off-engine';
import type { OrderLoopStatus, PlaceOrderResult } from '@/lib/chin-order-executor';
import type { OffEngineStatus } from '@/lib/chin-off-engine';
import type { ChinBotEngine } from '@/lib/chin-bot-engine';
import { getChinEngine } from '@/lib/chin-bot-engine';
import type { RiskEvaluation, RiskSymbolConfig } from '@/lib/chin-risk-controller';
import { acquireOrderLock, releaseOrderLock } from '@/lib/order-lock';

// ─── Types ──────────────────────────────────────────────────

export interface GlobalOrderState {
  symbolId: number;
  symbolName: string;
  status: OrderLoopStatus | null;
  abort: (() => void) | null;
  accountId: string;
}

export interface GlobalOffEngineState {
  symbolId: number;
  symbolName: string;
  status: OffEngineStatus | null;
  abort: (() => void) | null;
  accountId: string;
}

export type StatusListener = () => void;

// ─── Risk evaluation access ────────────────────────────────
// RiskController runs inside ChinMonitor via a React hook.
// We expose a module-level setter so the global workspace
// can read the latest risk evaluation without coupling to React.
//
// IMPORTANT: Risk evaluation is scoped per (strategyId, accountId).
// A single strategy can trade on MULTIPLE accounts simultaneously,
// and each account holds its OWN open positions. A single global
// risk evaluation caused cross-account contamination — account A's
// positions were blocking account B's orders (and vice versa).

const riskEvalByAccount = new Map<string, Map<string, RiskEvaluation>>();
const riskConfigsByAccount = new Map<string, Map<string, Record<number, RiskSymbolConfig>>>();

export function setGlobalRiskEvaluation(
  strategyId: string,
  accountId: string,
  evaluation: RiskEvaluation | null,
  configs?: Record<number, RiskSymbolConfig>,
): void {
  if (!strategyId) return;

  let accMap = riskEvalByAccount.get(strategyId);
  if (!accMap) {
    accMap = new Map();
    riskEvalByAccount.set(strategyId, accMap);
  }

  if (evaluation) {
    accMap.set(accountId, evaluation);
    if (configs) {
      let cfgMap = riskConfigsByAccount.get(strategyId);
      if (!cfgMap) {
        cfgMap = new Map();
        riskConfigsByAccount.set(strategyId, cfgMap);
      }
      cfgMap.set(accountId, configs);
    }
  } else {
    accMap.delete(accountId);
  }
}

export function clearAllGlobalRiskEvaluations(): void {
  riskEvalByAccount.clear();
  riskConfigsByAccount.clear();
}

function getGlobalRiskEvaluation(strategyId: string, accountId: string): RiskEvaluation | null {
  return riskEvalByAccount.get(strategyId)?.get(accountId) ?? null;
}

// ─── Realtime position counter (Safety Layer 2) ────────────
// Reads the TRUE open-position count for a symbol from the shared stream's
// latest positions snapshot, instead of the 8-second-polled cache.
// This catches pre-existing positions (from before this session) and any
// cache lag, so the order loop's "currentCount" is always accurate and the
// loop can never over-allocate beyond maxOrders TOTAL positions.
//
// Returns null if no fresh snapshot is available — the caller should fall back
// to the cached evaluation in that case (the local ordersPlaced hard cap still
// bounds the loop either way).

function countPositionsForSymbol(
  reconcileRes: Record<string, unknown>,
  symbolId: number,
): number {
  const posArray = Array.isArray(reconcileRes.position)
    ? (reconcileRes.position as Array<Record<string, unknown>>)
    : [];

  let count = 0;
  for (const p of posArray) {
    const td = (p.tradeData as Record<string, unknown>) || {};
    if (typeof td.symbolId === 'number' && td.symbolId === symbolId) {
      count++;
    }
  }
  return count;
}

async function fetchRealtimePositionCount(
  accountId: string,
  symbolId: number,
): Promise<number | null> {
  const numId = parseInt(accountId, 10);
  if (isNaN(numId)) return null;

  // ── Fast path: read the latest positions snapshot pushed by the shared
  // SSE stream. The stream reconciles positions on its single persistent
  // cTrader WebSocket, so this avoids opening a second connection.
  const cached = getLatestPositionsSnapshot(numId);
  if (cached && cached.reconcileRes && Date.now() - cached.timestamp < 10000) {
    return countPositionsForSymbol(cached.reconcileRes, symbolId);
  }

  // No fresh snapshot yet (stream not up) — return null so the caller falls
  // back to the cached risk evaluation (the local hard cap still bounds it).
  return null;
}

// ─── Distributed-lock order placement ─────────────────────
// Wraps a single place-order attempt in a Supabase-backed lease
// lock scoped to (strategy, account, symbol). Before sending the
// order to cTrader we RE-CHECK the true open-position count inside
// the lock, closing the "check-then-act" race that caused double
// orders when two devices fire BUY/SELL at the same time.
//
// Result reasons:
//   - 'locked'      → another device holds the lock right now
//   - 'max_reached' → the re-check saw slots already filled
async function placeOrderWithLock(
  strategyId: string,
  accountId: string,
  numAccountId: number,
  symbolName: string,
  maxOrders: number,
  params: { symbolId: number; tradeSide: 'BUY' | 'SELL'; volume: number },
  log: (level: 'info' | 'warn' | 'error' | 'success', symbol: string, message: string) => void,
): Promise<PlaceOrderResult> {
  const lockKey = `${strategyId}::${accountId}::${params.symbolId}`;

  const lock = await acquireOrderLock(lockKey, 20);
  if (!lock.acquired) {
    log('warn', symbolName, `[Lock] ${lockKey} held by another device — skipping this attempt`);
    return { success: false, error: 'Another device is placing an order right now', reason: 'locked' };
  }

  try {
    // Re-check the TRUE open-position count INSIDE the lock. If another
    // device just filled the remaining slots, skip so we never double-order.
    const freshCount = await fetchRealtimePositionCount(accountId, params.symbolId);
    if (freshCount !== null && freshCount >= maxOrders) {
      log('warn', symbolName, `[Lock] Max reached (${freshCount}/${maxOrders}) — another device likely placed`);
      return { success: false, error: 'Max orders already reached', reason: 'max_reached' };
    }

    log('info', symbolName, `[Lock] Placing ${params.tradeSide} vol=${params.volume} units`);

    const { data, error: invokeErr } = await supabase.functions.invoke<{
      success: boolean;
      executionEvent?: Record<string, unknown>;
      errorEvent?: Record<string, unknown>;
      error?: string;
    }>('ctrader-gateway', {
      method: 'POST',
      body: {
        action: 'place-order',
        accountId: numAccountId,
        symbolId: params.symbolId,
        orderType: 'MARKET',
        tradeSide: params.tradeSide,
        volume: params.volume,
      },
    });

    if (invokeErr) {
      return { success: false, error: invokeErr.message };
    }
    if (!data?.success) {
      const errDetail = data?.error || data?.errorEvent
        ? `cTrader rejected: ${(data.errorEvent as Record<string, unknown>)?.description || data?.error || 'unknown'}`
        : 'Order failed';
      return { success: false, error: errDetail };
    }
    return { success: true };
  } finally {
    await releaseOrderLock(lockKey, lock.ownerId);
  }
}

// ─── Module-level state (per strategyId → accountId → symbolId) ──

const orderStates = new Map<string, Map<string, Map<number, GlobalOrderState>>>();
const offEngineStates = new Map<string, Map<string, Map<number, GlobalOffEngineState>>>();
const statusListeners = new Set<StatusListener>();

function notifyListeners(): void {
  statusListeners.forEach((fn) => {
    try { fn(); } catch { /* ignore */ }
  });
}

// ─── Internal accessor helpers ─────────────────────────────

function getOrderAccountMap(
  strategyId: string,
  accountId: string,
  create: boolean,
): Map<number, GlobalOrderState> {
  let stratMap = orderStates.get(strategyId);
  if (!stratMap) {
    if (!create) return new Map();
    stratMap = new Map();
    orderStates.set(strategyId, stratMap);
  }
  let accMap = stratMap.get(accountId);
  if (!accMap) {
    if (!create) return new Map();
    accMap = new Map();
    stratMap.set(accountId, accMap);
  }
  return accMap;
}

function getOffAccountMap(
  strategyId: string,
  accountId: string,
  create: boolean,
): Map<number, GlobalOffEngineState> {
  let stratMap = offEngineStates.get(strategyId);
  if (!stratMap) {
    if (!create) return new Map();
    stratMap = new Map();
    offEngineStates.set(strategyId, stratMap);
  }
  let accMap = stratMap.get(accountId);
  if (!accMap) {
    if (!create) return new Map();
    accMap = new Map();
    stratMap.set(accountId, accMap);
  }
  return accMap;
}

// ─── Subscribe ─────────────────────────────────────────────

export function subscribeGlobalWorkspace(listener: StatusListener): () => void {
  statusListeners.add(listener);
  return () => { statusListeners.delete(listener); };
}

// ─── Engine Termination Notification ─────────────────────
// Fired when an order loop or off engine reaches a terminal phase
// on its own (completed / max_reached / auto_off / error). The UI
// subscribes to this to reset the corresponding button back to OFF —
// otherwise the button keeps showing BUY/SELL while the engine is dead.
// accountId lets the (per-account) UI reset only the right button.

type EngineTerminatedHandler = (strategyId: string, accountId: string, symbolId: number, kind: 'order' | 'off') => void;

const engineTerminatedHandlers = new Set<EngineTerminatedHandler>();

export function subscribeEngineTerminated(handler: EngineTerminatedHandler): () => void {
  engineTerminatedHandlers.add(handler);
  return () => { engineTerminatedHandlers.delete(handler); };
}

function notifyEngineTerminated(strategyId: string, accountId: string, symbolId: number, kind: 'order' | 'off'): void {
  engineTerminatedHandlers.forEach((fn) => {
    try { fn(strategyId, accountId, symbolId, kind); } catch { /* ignore */ }
  });
}

// ─── Order Loop Management ─────────────────────────────────

export function getGlobalOrderState(strategyId: string, accountId: string, symbolId: number): GlobalOrderState | undefined {
  return getOrderAccountMap(strategyId, accountId, false).get(symbolId);
}

export function getAllGlobalOrderStates(strategyId: string, accountId: string): Map<number, GlobalOrderState> {
  return getOrderAccountMap(strategyId, accountId, false);
}

export function startGlobalOrderLoop(
  strategyId: string,
  symbolId: number,
  symbolName: string,
  accountId: string,
  direction: 'buy' | 'sell',
  volume: number,
  maxOrders: number,
  pushLog?: (level: 'info' | 'warn' | 'error' | 'success', symbol: string, message: string) => void,
): boolean {
  const log = pushLog || (() => {});

  // Already running?
  const accMap = getOrderAccountMap(strategyId, accountId, false);
  const existing = accMap.get(symbolId);
  if (existing?.abort) {
    // Check if actually running (not finished). status may be null while the
    // loop is still initializing — treat that as "running" too so we never
    // orphan a live loop (dropping its abort handle without calling it).
    const finished = existing.status && ['completed', 'max_reached', 'error'].includes(existing.status.phase);
    if (!finished) {
      log('info', symbolName, `[GlobalWS] Order loop already running, skipping`);
      return false;
    }
    // Finished — clean up stale entry
    log('info', symbolName, `[GlobalWS] Cleaning up finished order loop`);
    existing.abort = null;
    existing.status = null;
  }

  const engine = getChinEngine(strategyId, accountId);
  if (!engine) {
    log('error', symbolName, `[GlobalWS] No ChinBotEngine found for strategy ${strategyId} account ${accountId}`);
    return false;
  }

  const numAccountId = parseInt(accountId, 10);
  if (isNaN(numAccountId)) {
    log('error', symbolName, `[GlobalWS] Invalid account ID: ${accountId}`);
    return false;
  }

  log('success', symbolName, `[GlobalWS] STARTING order loop: ${direction.toUpperCase()} vol=${volume} max=${maxOrders}`);

  const { abort } = startOrderLoop(
    {
      accountId,
      symbolId,
      symbolName,
      direction,
      volume,
      maxOrders,
    },
    {
      checkRisk: () => {
        const evalCheck = getGlobalRiskEvaluation(strategyId, accountId)?.checks[symbolId];
        if (!evalCheck) {
          return { allowed: false, reason: 'Risk evaluation not ready' };
        }
        return { allowed: evalCheck.allowed, reason: evalCheck.reason };
      },
      placeOrder: (params) => placeOrderWithLock(
        strategyId,
        accountId,
        numAccountId,
        symbolName,
        maxOrders,
        params,
        log,
      ),
      getPositionCount: async () => {
        const fresh = await fetchRealtimePositionCount(accountId, symbolId);
        if (fresh !== null) return fresh;
        return getGlobalRiskEvaluation(strategyId, accountId)?.positionCountBySymbol[symbolId] || 0;
      },
    },
    (status: OrderLoopStatus) => {
      // Write to global store
      const writeMap = getOrderAccountMap(strategyId, accountId, true);
      const existingEntry = writeMap.get(symbolId);
      writeMap.set(symbolId, {
        ...(existingEntry || { symbolId, symbolName, abort: null, accountId }),
        status,
      });

      // Notify listeners (ChinMonitor will re-render)
      notifyListeners();

      // Notify UI so the button resets when the loop finishes on its own
      if (status.phase === 'completed' || status.phase === 'max_reached') {
        notifyEngineTerminated(strategyId, accountId, symbolId, 'order');
      }

      // Log
      if (status.phase === 'error') {
        log('error', symbolName, `[GlobalWS] Loop error: ${status.lastError || status.message}`);
      } else if (status.phase === 'blocked') {
        log('warn', symbolName, `[GlobalWS] Loop blocked: ${status.message}`);
      } else if (status.phase === 'completed') {
        log('success', symbolName, `[GlobalWS] Loop completed: ${status.ordersPlaced} orders`);
      }
    },
  );

  // Store globally
  const writeMap = getOrderAccountMap(strategyId, accountId, true);
  writeMap.set(symbolId, {
    symbolId,
    symbolName,
    status: null,
    abort,
    accountId,
  });

  notifyListeners();
  return true;
}

export function abortGlobalOrderLoop(strategyId: string, accountId: string, symbolId: number): void {
  const accMap = getOrderAccountMap(strategyId, accountId, false);
  const entry = accMap.get(symbolId);
  if (entry?.abort) {
    entry.abort();
    entry.abort = null;
    entry.status = null;
    notifyListeners();
    console.log(`[GlobalWS] 🛑 Aborted order loop: ${entry.symbolName} (strategy=${strategyId} account=${accountId})`);
  }
}

export function abortAllGlobalOrderLoops(strategyId: string, accountId: string): void {
  const accMap = getOrderAccountMap(strategyId, accountId, false);
  if (accMap.size === 0) return;
  let count = 0;
  accMap.forEach((entry, symId) => {
    if (entry.abort) {
      entry.abort();
      entry.abort = null;
      entry.status = null;
      count++;
    }
  });
  if (count > 0) {
    notifyListeners();
    console.log(`[GlobalWS] 🛑 Aborted ${count} order loop(s) for strategy=${strategyId} account=${accountId}`);
  }
}

// ─── Off Engine Management ─────────────────────────────────

export function getGlobalOffEngineState(strategyId: string, accountId: string, symbolId: number): GlobalOffEngineState | undefined {
  return getOffAccountMap(strategyId, accountId, false).get(symbolId);
}

export function getAllGlobalOffEngineStates(strategyId: string, accountId: string): Map<number, GlobalOffEngineState> {
  return getOffAccountMap(strategyId, accountId, false);
}

export function startGlobalOffEngine(
  strategyId: string,
  symbolId: number,
  symbolName: string,
  accountId: string,
  direction: 'buy' | 'sell',
  conditionPrice: number,
  volume: number,
  maxOrders: number,
  pushLog?: (level: 'info' | 'warn' | 'error' | 'success', symbol: string, message: string) => void,
): boolean {
  const log = pushLog || (() => {});

  // Already running?
  const accMap = getOffAccountMap(strategyId, accountId, false);
  const existing = accMap.get(symbolId);
  if (existing?.abort) {
    const finished = existing.status && ['completed', 'auto_off', 'error'].includes(existing.status.phase);
    if (!finished) {
      log('info', symbolName, `[GlobalWS] Off engine already running, skipping`);
      return false;
    }
    log('info', symbolName, `[GlobalWS] Cleaning up finished off engine`);
    existing.abort = null;
    existing.status = null;
  }

  const engine = getChinEngine(strategyId, accountId);
  if (!engine) {
    log('error', symbolName, `[GlobalWS] No ChinBotEngine for strategy ${strategyId} account ${accountId}`);
    return false;
  }

  const numAccountId = parseInt(accountId, 10);
  if (isNaN(numAccountId)) {
    log('error', symbolName, `[GlobalWS] Invalid account ID: ${accountId}`);
    return false;
  }

  log('success', symbolName, `[GlobalWS] STARTING off engine: ${direction.toUpperCase()} trigger=${conditionPrice} vol=${volume} max=${maxOrders}`);

  const { abort: offAbort } = startOffEngine(
    {
      accountId,
      symbolId,
      symbolName,
      direction,
      conditionPrice,
      volume,
      maxOrders,
    },
    {
      getCurrentBid: () => {
        const buf = engine.snapshot().symbols[symbolId];
        return buf?.currentBid ?? null;
      },
      checkRisk: () => {
        const evalCheck = getGlobalRiskEvaluation(strategyId, accountId)?.checks[symbolId];
        if (!evalCheck) {
          return { allowed: false, reason: 'Risk evaluation not ready' };
        }
        return { allowed: evalCheck.allowed, reason: evalCheck.reason };
      },
      placeOrder: (params) => placeOrderWithLock(
        strategyId,
        accountId,
        numAccountId,
        symbolName,
        maxOrders,
        params,
        log,
      ),
      getPositionCount: async () => {
        const fresh = await fetchRealtimePositionCount(accountId, symbolId);
        if (fresh !== null) return fresh;
        return getGlobalRiskEvaluation(strategyId, accountId)?.positionCountBySymbol[symbolId] || 0;
      },
      onAutoOff: (symIdToOff: number) => {
        log('warn', symbolName, `[GlobalWS] Off engine auto-OFF: price already past trigger`);
        // Clean up global state
        const cleanupMap = getOffAccountMap(strategyId, accountId, false);
        const entry = cleanupMap.get(symIdToOff);
        if (entry) {
          entry.abort = null;
          entry.status = null;
        }
        notifyListeners();
        // Also abort any running order loop this engine may have spawned
        abortGlobalOrderLoop(strategyId, accountId, symIdToOff);
      },
    },
    (status: OffEngineStatus) => {
      const writeMap = getOffAccountMap(strategyId, accountId, true);
      const existingEntry = writeMap.get(symbolId);
      writeMap.set(symbolId, {
        ...(existingEntry || { symbolId, symbolName, abort: null, accountId }),
        status,
      });
      notifyListeners();

      // Notify UI so the button resets when the off engine finishes on its own
      if (status.phase === 'auto_off' || status.phase === 'completed' || status.phase === 'error') {
        notifyEngineTerminated(strategyId, accountId, symbolId, 'off');
      }

      if (status.phase === 'auto_off') {
        log('warn', symbolName, `[GlobalWS][Off] ${status.message}`);
      } else if (status.phase === 'triggered') {
        log('success', symbolName, `[GlobalWS][Off] PRICE HIT! ${status.message}`);
      } else if (status.phase === 'completed') {
        log('success', symbolName, `[GlobalWS][Off] ${status.message}`);
      } else if (status.phase === 'error') {
        log('error', symbolName, `[GlobalWS][Off] ERROR: ${status.message}`);
      }
    },
  );

  const writeMap = getOffAccountMap(strategyId, accountId, true);
  writeMap.set(symbolId, {
    symbolId,
    symbolName,
    status: null,
    abort: offAbort,
    accountId,
  });

  notifyListeners();
  return true;
}

export function abortGlobalOffEngine(strategyId: string, accountId: string, symbolId: number): void {
  const accMap = getOffAccountMap(strategyId, accountId, false);
  const entry = accMap.get(symbolId);
  if (entry?.abort) {
    entry.abort();
    entry.abort = null;
    entry.status = null;
    // Also abort any running order loop spawned by this off engine
    abortGlobalOrderLoop(strategyId, accountId, symbolId);
    notifyListeners();
    console.log(`[GlobalWS] 🛑 Aborted off engine: ${entry.symbolName} (strategy=${strategyId} account=${accountId})`);
  }
}

export function abortAllGlobalOffEngines(strategyId: string, accountId: string): void {
  const accMap = getOffAccountMap(strategyId, accountId, false);
  if (accMap.size === 0) return;
  let count = 0;
  accMap.forEach((entry, symId) => {
    if (entry.abort) {
      entry.abort();
      entry.abort = null;
      entry.status = null;
      count++;
      // Also abort any running order loop
      abortGlobalOrderLoop(strategyId, accountId, symId);
    }
  });
  if (count > 0) {
    notifyListeners();
    console.log(`[GlobalWS] 🛑 Aborted ${count} off engine(s) for strategy=${strategyId} account=${accountId}`);
  }
}

// ─── Full cleanup ──────────────────────────────────────────

export function abortAllGlobalEngines(strategyId: string, accountId: string): void {
  abortAllGlobalOrderLoops(strategyId, accountId);
  abortAllGlobalOffEngines(strategyId, accountId);
}

export function cleanupGlobalWorkspace(strategyId: string, accountId: string): void {
  abortAllGlobalEngines(strategyId, accountId);
  const stratMap = orderStates.get(strategyId);
  if (stratMap) {
    stratMap.delete(accountId);
    if (stratMap.size === 0) orderStates.delete(strategyId);
  }
  const offStratMap = offEngineStates.get(strategyId);
  if (offStratMap) {
    offStratMap.delete(accountId);
    if (offStratMap.size === 0) offEngineStates.delete(strategyId);
  }
}

// ─── Bulk start from workspace data ────────────────────────

export interface WorkspaceSymbolConfig {
  conditionPrice: string;
  maxOrders: string;
  direction: 'buy' | 'sell' | null;
  orderDirection: 'buy' | 'sell' | null;
  orderVolume: string;
  boxX?: string;
  boxAlertOn?: boolean;
}

export function applyGlobalWorkspace(
  strategyId: string,
  accountId: string,
  symbolConfigs: Record<number, WorkspaceSymbolConfig>,
  symbolNames: Record<number, string>,
  pushLog?: (level: 'info' | 'warn' | 'error' | 'success', symbol: string, message: string) => void,
): { ordersStarted: number; offEnginesStarted: number } {
  const log = pushLog || (() => {});
  let ordersStarted = 0;
  let offEnginesStarted = 0;

  for (const [symIdStr, config] of Object.entries(symbolConfigs)) {
    const symId = Number(symIdStr);
    const symName = symbolNames[symId] || `Symbol #${symId}`;

    // Off Engine (limit order watcher)
    if (config.direction) {
      const price = parseFloat(config.conditionPrice) || 0;
      const max = parseInt(config.maxOrders, 10) || 0;
      const vol = parseFloat(config.orderVolume) || 0;

      if (price > 0 && max > 0 && vol > 0) {
        const started = startGlobalOffEngine(
          strategyId, symId, symName, accountId,
          config.direction, price, vol, max, log,
        );
        if (started) offEnginesStarted++;
      }
    }

    // Order Now (immediate order loop)
    if (config.orderDirection) {
      const max = parseInt(config.maxOrders, 10) || 0;
      const vol = parseFloat(config.orderVolume) || 0;

      if (max > 0 && vol > 0) {
        const started = startGlobalOrderLoop(
          strategyId, symId, symName, accountId,
          config.orderDirection, vol, max, log,
        );
        if (started) ordersStarted++;
      }
    }
  }

  if (ordersStarted > 0 || offEnginesStarted > 0) {
    log('success', 'SYSTEM', `[GlobalWS] Applied workspace: ${offEnginesStarted} off engine(s) + ${ordersStarted} order loop(s) started`);
  }

  return { ordersStarted, offEnginesStarted };
}